// =============================================================================
// La géométrie du graphique « Puissances par poste » (D14, décisions v1.2 §B).
//
// Ce que le lecteur doit vivre : chaque valeur tombe à sa place sur un axe
// commun, écrite en gras au bout de sa barre, à l'extérieur, là où aucun trait
// ne la raye ; la réserve se voit en vert dans la barre, le dépassement en
// rouge dans son prolongement (N1 du 19/10/2026) ; la carte se lit aussi bien
// à 1 440 px (libellé à gauche des barres) qu'à 560 px (libellé au-dessus).
//
// Fonctions pures : à partir du modèle (`optim-turpe-v2.graphique.ts`) et de la
// largeur disponible, elles rendent des primitives (rectangles, traits, textes)
// que le composant pose telles quelles dans un SVG dessiné à la main. L'ordre
// de dessin est celui de la greffe n° 1 du jugement : barres, puis trait du
// pic, puis textes avec halo.
// =============================================================================
import { nombreGraphique } from "@modules/courtage/utils/optim-turpe-v2.graphique"
import type {
    BarreGraphique,
    ModeleGraphique,
    PosteGraphique,
} from "@modules/courtage/utils/optim-turpe-v2.graphique"

/** En dessous de cette largeur (px), le libellé passe au-dessus des barres (528 px). */
export const LARGEUR_ETROITE = 720

/** Hauteur d'une barre et pas entre deux barres d'un même poste (px). */
const BARRE_H = 14
const BARRE_PAS = 18

/** Les couleurs, en jetons du thème (`--p-*`), jamais en dur. */
export const COULEUR = {
    aujourdhui: "var(--p-primary-200)",
    reserveClaire: "var(--p-green-200)",
    depassementClair: "var(--p-red-300)",
    recommande: "var(--p-primary-700)",
    saisie: "var(--p-primary-500)",
    reserveFoncee: "var(--p-green-600)",
    reserveMoyenne: "var(--p-green-500)",
    depassementFonce: "var(--p-red-600)",
    depassementMoyen: "var(--p-red-500)",
    marge: "var(--p-primary-300)",
    trait: "var(--p-primary-900)",
    texteFonce: "var(--p-primary-900)",
    blanc: "var(--p-surface-0)",
    grille: "var(--p-surface-200)",
    axe: "var(--p-surface-300)",
    axeTexte: "var(--p-surface-500)",
    poste: "var(--p-primary-700)",
    relevee: "var(--p-primary-500)",
    raccordement: "var(--p-surface-600)",
    raccordementEcarte: "var(--p-orange-500)",
    raccordementEcarteTexte: "var(--p-orange-700)",
} as const

export type TexteDessin = {
    x: number
    y: number
    texte: string
    ancre: "start" | "middle" | "end"
    taille: number
    graisse: number
    couleur: string
    /** La couleur du halo (contour peint sous le texte), `null` sans halo. */
    halo: string | null
}

export type RectDessin = {
    x: number
    y: number
    l: number
    h: number
    couleur: string
    /** La portion : base, marge, réserve ou dépassement (pour les tests). */
    role: "base" | "marge" | "reserve" | "depassement"
}

export type BarreDessin = {
    nature: BarreGraphique["nature"]
    texte: string
    rects: RectDessin[]
    /** La valeur de la barre, au bout, à l'extérieur (N1). */
    valeur: TexteDessin
    /** Ce que le remplissage montre : réserve, dépassement, disjonction ou rien. */
    sens: string | null
}

export type PosteDessin = {
    cle: string
    /** Le libellé lu par un lecteur d'écran (« Heures pleines de saison haute (HPH) »). */
    titre: string
    libelles: TexteDessin[]
    relevee: TexteDessin | null
    barres: BarreDessin[]
    trait: { x: number; y1: number; y2: number; pointille: boolean } | null
    pic: TexteDessin | null
    repere: { cx: number; cy: number } | null
}

export type Dessin = {
    largeur: number
    hauteur: number
    etroit: boolean
    grille: { x: number; y1: number; y2: number }[]
    axe: { x1: number; x2: number; y: number }
    graduations: TexteDessin[]
    raccordement: {
        ligne: { x: number; y1: number; y2: number } | null
        texte: TexteDessin
        ecarte: boolean
    } | null
    postes: PosteDessin[]
}

/**
 * Largeur approchée d'un texte Inter (px), reprise de la maquette : elle
 * réserve la place des valeurs à droite des barres. `graisse` 700 élargit.
 */
export function largeurTexte(s: string, taille: number, graisse = 400): number {
    let w = 0
    for (const ch of s) {
        if (/\d/.test(ch)) w += 0.6
        else if (ch === " " || ch === "\u00a0") w += 0.28
        else if (",.:;'’".includes(ch)) w += 0.27
        else if ("()".includes(ch)) w += 0.34
        else if ("mwMW".includes(ch)) w += 0.88
        else if (ch !== ch.toLowerCase()) w += 0.68
        else if ("iljtfr".includes(ch)) w += 0.33
        else w += 0.56
    }
    return w * taille * (graisse >= 700 ? 1.06 : 1)
}

/** Les trois teintes d'une barre : base, réserve (vert), dépassement (rouge). */
function couleursBarre(
    nature: BarreGraphique["nature"],
    colonne: string | null
): { base: string; reserve: string; depassement: string } {
    if (nature === "aujourdhui")
        return {
            base: COULEUR.aujourdhui,
            reserve: COULEUR.reserveClaire,
            depassement: COULEUR.depassementClair,
        }
    return colonne === "Recommandé"
        ? {
              base: COULEUR.recommande,
              reserve: COULEUR.reserveFoncee,
              depassement: COULEUR.depassementFonce,
          }
        : {
              base: COULEUR.saisie,
              reserve: COULEUR.reserveMoyenne,
              depassement: COULEUR.depassementMoyen,
          }
}

type Cadre = {
    x0: number
    largeurDonnees: number
    max: number
    etroit: boolean
    taille: number
    unite: string
    incomplete: boolean
    colonne: string | null
    largeur: number
}

function abscisse(c: Cadre, v: number): number {
    return c.x0 + (Math.min(Math.max(v, 0), c.max) / c.max) * c.largeurDonnees
}

/**
 * Les portions d'une barre (N1) : la barre garde la longueur de la puissance
 * souscrite. Pic dessous : teinte de base de 0 au pic, marge de sécurité en
 * bleu clair (barre recommandée), réserve en vert jusqu'au bout. Pic au-delà :
 * la barre entière en teinte de base, puis le dépassement en rouge dans son
 * prolongement, jusqu'au trait.
 */
function rectsDeLaBarre(
    c: Cadre,
    barre: BarreGraphique,
    pic: number | null,
    y: number
): RectDessin[] {
    const couleurs = couleursBarre(barre.nature, c.colonne)
    const rect = (
        de: number,
        a: number,
        couleur: string,
        role: RectDessin["role"]
    ): RectDessin => {
        const x = abscisse(c, de)
        return {
            x,
            y,
            l: Math.max(0, abscisse(c, a) - x),
            h: BARRE_H,
            couleur,
            role,
        }
    }
    const v = barre.valeur
    if (pic === null || c.incomplete || !barre.ecart)
        return [rect(0, v, couleurs.base, "base")]
    if (barre.ecart.sens === "reserve") {
        const rects = [rect(0, pic, couleurs.base, "base")]
        const debutReserve = barre.marge ? barre.marge.a : pic
        if (barre.marge)
            rects.push(rect(pic, barre.marge.a, COULEUR.marge, "marge"))
        if (v > debutReserve)
            rects.push(rect(debutReserve, v, couleurs.reserve, "reserve"))
        return rects
    }
    return [
        rect(0, v, couleurs.base, "base"),
        rect(v, pic, couleurs.depassement, "depassement"),
    ]
}

/**
 * La valeur de la barre (N1) : au bout, à l'extérieur, en gras, sans unité
 * (l'unité est sur l'axe). Elle commence après la barre ET après le trait du
 * pic : aucun trait ne la raye, même sous une mesure incomplète.
 */
function valeurDeLaBarre(
    c: Cadre,
    barre: BarreGraphique,
    pic: number | null,
    y: number
): TexteDessin {
    const xv = abscisse(c, barre.valeur)
    const xp = pic !== null ? abscisse(c, pic) : xv
    return {
        x: Math.max(xv, xp + (xp > xv ? 2 : 0)) + 6,
        y: y + 10.5,
        texte: nombreGraphique(barre.valeur),
        ancre: "start",
        taille: c.taille,
        graisse: 700,
        couleur: COULEUR.texteFonce,
        halo: COULEUR.blanc,
    }
}

/** La place que prennent, à droite, les valeurs écrites au bout des barres. */
function margeDroite(modele: ModeleGraphique, taille: number): number {
    const valeurs = modele.postes.flatMap((p) =>
        p.barres.map(
            (b) => largeurTexte(nombreGraphique(b.valeur), taille, 700) + 10
        )
    )
    const pic = Math.max(
        0,
        ...modele.postes.map((p) =>
            p.pic === null
                ? 0
                : largeurTexte(
                      `${nombreGraphique(p.pic)}\u00a0${modele.unite}`,
                      taille,
                      600
                  ) / 2
        )
    )
    return Math.min(120, Math.max(24, pic + 4, ...valeurs))
}

/** Les libellés d'un poste et sa mention « relevée » (choix 2). */
function libellesDuPoste(
    poste: PosteGraphique,
    etroit: boolean,
    largeur: number,
    haut: number,
    milieuBarres: number
): { libelles: TexteDessin[]; relevee: TexteDessin | null } {
    const texte = (
        x: number,
        y: number,
        t: string,
        taille: number,
        graisse: number,
        couleur: string,
        ancre: TexteDessin["ancre"] = "start"
    ): TexteDessin => ({
        x,
        y,
        texte: t,
        ancre,
        taille,
        graisse,
        couleur,
        halo: null,
    })
    const mention = "relevée au niveau du poste précédent"
    if (etroit) {
        // Le code reste entre parenthèses, à côté du libellé en clair (C16).
        const entete = poste.code ? `${poste.nom} (${poste.code})` : poste.nom
        // À droite de l'entête de rangée (choix 2) ; dessous quand la place
        // manque, pour ne jamais chevaucher l'entête.
        const aDroite =
            largeurTexte(entete, 12) + largeurTexte(mention, 11) + 12 <= largeur
        return {
            libelles: [texte(0, haut + 12, entete, 12, 500, COULEUR.poste)],
            relevee: poste.relevee
                ? aDroite
                    ? texte(
                          largeur,
                          haut + 12,
                          mention,
                          11,
                          500,
                          COULEUR.relevee,
                          "end"
                      )
                    : texte(0, haut + 26, mention, 11, 500, COULEUR.relevee)
                : null,
        }
    }
    const lignes: TexteDessin[] = []
    const n = 1 + (poste.code ? 1 : 0) + (poste.relevee ? 1 : 0)
    let y = milieuBarres - ((n - 1) * 15) / 2 + 5
    lignes.push(texte(0, y, poste.nom, 14, 400, COULEUR.poste))
    if (poste.code) {
        y += 15
        lignes.push(texte(0, y, `(${poste.code})`, 12, 600, COULEUR.poste))
    }
    let relevee: TexteDessin | null = null
    if (poste.relevee) {
        y += 15
        relevee = texte(0, y, mention, 11, 500, COULEUR.relevee)
    }
    return { libelles: lignes, relevee }
}

/** Le pic : sa valeur avec son unité (choix 10, exception), au-dessus du trait. */
function picDuPoste(c: Cadre, pic: number, y: number): TexteDessin {
    const texte = `${nombreGraphique(pic)}\u00a0${c.unite}`
    const w = largeurTexte(texte, c.taille)
    const x = abscisse(c, pic)
    let ancre: TexteDessin["ancre"] = "middle"
    let xt = x
    if (x - w / 2 < c.x0) {
        ancre = "start"
        xt = c.x0
    } else if (x + w / 2 > c.largeur) {
        ancre = "end"
        xt = c.largeur
    }
    return {
        x: xt,
        y,
        texte,
        ancre,
        taille: c.taille,
        graisse: 600,
        couleur: COULEUR.texteFonce,
        halo: COULEUR.blanc,
    }
}

/**
 * Le dessin complet, à une largeur donnée (px). `null` quand rien n'est
 * dessinable (aucun poste, aucune échelle).
 */
export function dessinerGraphique(
    modele: ModeleGraphique,
    largeur: number
): Dessin | null {
    if (!modele.postes.length || !modele.echelle) return null
    const etroit = largeur < LARGEUR_ETROITE
    const taille = etroit ? 11 : 12
    const labelW = etroit ? 0 : largeur >= 900 ? 236 : 200
    const droite = margeDroite(modele, taille)
    const c: Cadre = {
        x0: labelW,
        largeurDonnees: Math.max(40, largeur - labelW - droite),
        max: modele.echelle.max,
        etroit,
        taille,
        unite: modele.unite,
        incomplete: modele.incomplete,
        colonne: modele.colonne,
        largeur,
    }
    const enTete = modele.raccordement ? 20 : 4
    let y = enTete
    const postes: PosteDessin[] = []
    for (const poste of modele.postes) {
        const haut = y
        const zonePic = 15
        const n = Math.max(1, poste.barres.length)
        const hauteurBarres = n * BARRE_PAS - (BARRE_PAS - BARRE_H)
        // À 1 040 px, les libellés se centrent sur les barres ; à 528 px,
        // l'entête de rangée (et la mention dessous si elle n'y tient pas)
        // précède les barres.
        const provisoire = libellesDuPoste(poste, etroit, largeur, haut, 0)
        const mentionDessous =
            etroit &&
            provisoire.relevee !== null &&
            provisoire.relevee.ancre === "start"
        const ligneEntete = etroit ? (mentionDessous ? 32 : 18) : 0
        const hautBarres = haut + ligneEntete + zonePic
        const milieu = hautBarres + hauteurBarres / 2
        const { libelles, relevee } = etroit
            ? provisoire
            : libellesDuPoste(poste, etroit, largeur, haut, milieu)
        const barres: BarreDessin[] = poste.barres.map((barre, j) => {
            const yb = hautBarres + j * BARRE_PAS
            return {
                nature: barre.nature,
                texte: barre.texte,
                rects: rectsDeLaBarre(c, barre, poste.pic, yb),
                valeur: valeurDeLaBarre(c, barre, poste.pic, yb),
                sens:
                    barre.ecart && !c.incomplete && poste.pic !== null
                        ? barre.ecart.sens
                        : null,
            }
        })
        const iPropose = poste.barres.findIndex((b) => b.nature === "propose")
        const bas = Math.max(
            hautBarres + hauteurBarres + 7,
            ...libelles.map((l) => l.y + 8),
            relevee && !etroit ? relevee.y + 8 : 0
        )
        postes.push({
            cle: poste.cle,
            titre: poste.code ? `${poste.nom} (${poste.code})` : poste.nom,
            libelles,
            relevee,
            barres,
            trait:
                poste.pic !== null
                    ? {
                          x: abscisse(c, poste.pic),
                          y1: hautBarres - 4,
                          y2: hautBarres + hauteurBarres + 3,
                          pointille: modele.incomplete,
                      }
                    : null,
            pic:
                poste.pic !== null
                    ? picDuPoste(c, poste.pic, hautBarres - 6)
                    : null,
            repere:
                poste.repereConseil !== null && iPropose >= 0
                    ? {
                          cx: abscisse(c, poste.repereConseil),
                          cy: hautBarres + iPropose * BARRE_PAS + BARRE_H / 2,
                      }
                    : null,
        })
        y = bas
    }
    const yAxe = y + 4
    const graduations = modele.echelle.graduations
    const dernier = graduations.length - 1
    return {
        largeur,
        hauteur: yAxe + 22,
        etroit,
        grille: graduations.map((g) => ({
            x: abscisse(c, g),
            y1: enTete,
            y2: yAxe,
        })),
        axe: { x1: c.x0, x2: abscisse(c, c.max), y: yAxe },
        graduations: graduations.map((g, i) => ({
            x: abscisse(c, g),
            y: yAxe + 15,
            texte:
                i === dernier
                    ? `${nombreGraphique(g)}\u00a0${modele.unite}`
                    : nombreGraphique(g),
            ancre: i === 0 ? "start" : i === dernier ? "end" : "middle",
            taille: 11,
            graisse: 400,
            couleur: COULEUR.axeTexte,
            halo: null,
        })),
        raccordement: raccordementDessine(modele, c, yAxe),
        postes,
    }
}

/** Le raccordement : pointillé à sa valeur, ou mention hors échelle (choix 3). */
function raccordementDessine(
    modele: ModeleGraphique,
    c: Cadre,
    yAxe: number
): Dessin["raccordement"] {
    const r = modele.raccordement
    if (!r) return null
    const n = nombreGraphique(r.valeur)
    if (!r.dansEchelle)
        return {
            ligne: null,
            ecarte: r.ecarte,
            texte: {
                x: c.largeur,
                y: 12,
                texte: `raccordement ${n}\u00a0${r.unite}, hors échelle →`,
                ancre: "end",
                taille: 11,
                graisse: 500,
                couleur: COULEUR.raccordement,
                halo: COULEUR.blanc,
            },
        }
    const x = abscisse(c, r.valeur)
    const aGauche = x < c.x0 + 0.55 * c.largeurDonnees
    return {
        ligne: { x, y1: 4, y2: yAxe },
        ecarte: r.ecarte,
        texte: {
            x: aGauche ? x + 5 : x - 5,
            y: 12,
            // Contrat v1.2 : « raccordement 78 kVA, écarté ».
            texte: `raccordement ${n}\u00a0${r.unite}${r.ecarte ? ", écarté" : ""}`,
            ancre: aGauche ? "start" : "end",
            taille: 11,
            graisse: 500,
            couleur: r.ecarte
                ? COULEUR.raccordementEcarteTexte
                : COULEUR.raccordement,
            halo: COULEUR.blanc,
        },
    }
}
